'use client'

import { useCallback, useState } from 'react'
import { motion } from 'framer-motion'
import { MapPin, Search, Plus, Trash2, ArrowUpDown } from 'lucide-react'
import {
  lookupBahRates,
  type BahLookupResult,
  type BahRankRate,
} from '@/app/actions'

/* ── Constants ─────────────────────────────────────────── */
const HIGHLIGHT_RANKS = ['E-3', 'E-4', 'E-5', 'E-6', 'E-7', 'O-1', 'O-2', 'O-3']

/* ── Rate table ────────────────────────────────────────── */
function RateTable({
  result,
  highlightRank,
  onSelectRank,
}: {
  result: BahLookupResult
  highlightRank: string | null
  onSelectRank: (rank: string) => void
}) {
  const [showDependents, setShowDependents] = useState(true)

  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
        <div>
          <h2 className="text-sm font-bold text-[var(--text-1)]">
            {result.city}, {result.state}
          </h2>
          <p className="text-xs text-[var(--text-3)]">ZIP {result.zip} · {result.rates.length} ranks</p>
        </div>
        <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
          {[true, false].map((val) => (
            <button
              key={String(val)}
              onClick={() => setShowDependents(val)}
              className={`px-3 py-1.5 text-[11px] font-semibold transition ${
                showDependents === val
                  ? 'bg-blue-600 text-white'
                  : 'bg-[var(--surface-2)] text-[var(--text-3)] hover:text-[var(--text-1)]'
              }`}
            >
              {val ? 'W/ Dep' : 'W/O Dep'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="max-h-[520px] overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="sticky top-0 bg-[var(--surface-2)]/80 backdrop-blur-sm">
              <th className="px-6 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[var(--text-3)]">
                Rank
              </th>
              <th className="px-6 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-[var(--text-3)]">
                {showDependents ? 'With Dependents' : 'Without Dependents'}
              </th>
              <th className="px-6 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-[var(--text-3)]">
                Annual
              </th>
            </tr>
          </thead>
          <tbody>
            {result.rates.map((rate, idx) => {
              const amount = showDependents ? rate.withDependents : rate.withoutDependents
              const isHighlighted = rate.rank === highlightRank
              const isEnlistedOfficerBoundary =
                idx > 0 && rate.rank.charAt(0) !== result.rates[idx - 1].rank.charAt(0)

              return (
                <tr
                  key={rate.rank}
                  onClick={() => onSelectRank(rate.rank)}
                  className={`cursor-pointer border-t transition ${
                    isEnlistedOfficerBoundary ? 'border-t-2 border-[var(--border)]' : 'border-[var(--border)]/50'
                  } ${
                    isHighlighted
                      ? 'bg-blue-500/10'
                      : 'hover:bg-[var(--surface-2)]/50'
                  }`}
                >
                  <td className="px-6 py-3">
                    <span
                      className={`text-sm font-semibold ${
                        isHighlighted ? 'text-blue-500' : 'text-[var(--text-1)]'
                      }`}
                    >
                      {rate.rank}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="text-sm font-bold text-[var(--text-1)]">
                      ${amount.toLocaleString()}
                    </span>
                    <span className="text-xs text-[var(--text-3)]">/mo</span>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="text-sm text-[var(--text-3)]">
                      ${(amount * 12).toLocaleString()}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ── Comparison bar chart ──────────────────────────────── */
function ComparisonChart({
  locations,
  selectedRank,
}: {
  locations: BahLookupResult[]
  selectedRank: string
}) {
  const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444']

  // Get the rate for the selected rank at each location
  const data = locations.map((loc, i) => {
    const rate = loc.rates.find((r) => r.rank === selectedRank)
    return {
      label: `${loc.city}, ${loc.state}`,
      withDep: rate?.withDependents ?? 0,
      withoutDep: rate?.withoutDependents ?? 0,
      color: COLORS[i % COLORS.length],
    }
  })

  const maxVal = Math.max(...data.map((d) => Math.max(d.withDep, d.withoutDep)), 1)

  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
      <h2 className="mb-1 text-sm font-bold text-[var(--text-1)]">
        BAH Comparison — {selectedRank}
      </h2>
      <p className="mb-5 text-xs text-[var(--text-3)]">
        Side-by-side monthly rates across your selected locations
      </p>

      <div className="space-y-4">
        {data.map((d, i) => (
          <div key={i}>
            <p className="mb-1.5 text-xs font-semibold text-[var(--text-1)]">{d.label}</p>
            {/* With dependents */}
            <div className="mb-1 flex items-center gap-3">
              <span className="w-14 text-[10px] text-[var(--text-3)]">W/ Dep</span>
              <div className="relative h-7 flex-1 overflow-hidden rounded-lg bg-[var(--surface-2)]">
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-lg"
                  style={{ backgroundColor: d.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${(d.withDep / maxVal) * 100}%` }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                />
                <span className="relative z-10 flex h-full items-center pl-3 text-xs font-bold text-white drop-shadow-sm">
                  ${d.withDep.toLocaleString()}
                </span>
              </div>
            </div>
            {/* Without dependents */}
            <div className="flex items-center gap-3">
              <span className="w-14 text-[10px] text-[var(--text-3)]">W/O Dep</span>
              <div className="relative h-7 flex-1 overflow-hidden rounded-lg bg-[var(--surface-2)]">
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-lg opacity-60"
                  style={{ backgroundColor: d.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${(d.withoutDep / maxVal) * 100}%` }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                />
                <span className="relative z-10 flex h-full items-center pl-3 text-xs font-bold text-white drop-shadow-sm">
                  ${d.withoutDep.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delta callout when exactly 2 locations */}
      {data.length === 2 && (
        <div className="mt-5 rounded-xl border border-[var(--border)] bg-[var(--surface-2)]/50 p-4 text-center">
          <p className="text-xs text-[var(--text-3)]">Monthly Difference (W/ Dep)</p>
          <p className={`mt-1 text-xl font-bold ${
            data[0].withDep > data[1].withDep ? 'text-emerald-500' : 'text-red-500'
          }`}>
            {data[0].withDep > data[1].withDep ? '+' : '-'}$
            {Math.abs(data[0].withDep - data[1].withDep).toLocaleString()}/mo
          </p>
          <p className="mt-0.5 text-[10px] text-[var(--text-3)]">
            {data[0].withDep > data[1].withDep ? data[0].label : data[1].label} pays more
          </p>
        </div>
      )}
    </div>
  )
}

/* ── Main component ────────────────────────────────────── */
export default function BAHLookup() {
  const [zipInputs, setZipInputs] = useState<string[]>([''])
  const [results, setResults] = useState<BahLookupResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedRank, setSelectedRank] = useState<string>('E-5')

  const runLookup = useCallback(async () => {
    const validZips = zipInputs.filter((z) => z.length === 5)
    if (validZips.length === 0) return

    setLoading(true)
    setError(null)
    try {
      const res = await Promise.all(
        validZips.map((zip) => lookupBahRates({ zip }))
      )
      setResults(res)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Lookup failed')
    } finally {
      setLoading(false)
    }
  }, [zipInputs])

  const updateZip = (idx: number, val: string) => {
    const next = [...zipInputs]
    next[idx] = val.replace(/\D/g, '').slice(0, 5)
    setZipInputs(next)
  }

  const addZip = () => {
    if (zipInputs.length < 5) setZipInputs([...zipInputs, ''])
  }

  const removeZip = (idx: number) => {
    if (zipInputs.length > 1) {
      setZipInputs(zipInputs.filter((_, i) => i !== idx))
      setResults(results.filter((_, i) => i !== idx))
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--text-1)]">BAH Lookup</h1>
        <p className="mt-1 text-sm text-[var(--text-3)]">
          Look up Basic Allowance for Housing rates by ZIP code. Add multiple locations to compare.
        </p>
      </div>

      {/* Search panel */}
      <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-6 shadow-sm">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-[var(--text-1)]">
          <MapPin className="h-4 w-4 text-blue-500" /> Duty Stations
        </h2>

        <div className="space-y-3">
          {zipInputs.map((zip, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-[var(--text-3)]">
                  ZIP
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={zip}
                  onChange={(e) => updateZip(idx, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runLookup() }}
                  placeholder="e.g. 76544"
                  className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-2)] py-3 pl-11 pr-4 text-sm font-medium text-[var(--text-1)] shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              {/* Show location label if we have a result */}
              {results[idx] && (
                <span className="hidden text-xs font-medium text-[var(--text-3)] sm:inline">
                  {results[idx].city}, {results[idx].state}
                </span>
              )}
              {zipInputs.length > 1 && (
                <button
                  onClick={() => removeZip(idx)}
                  className="rounded-md p-1.5 text-[var(--text-3)] hover:bg-red-500/10 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-3">
          {zipInputs.length < 5 && (
            <button
              onClick={addZip}
              className="flex items-center gap-1.5 rounded-lg border border-dashed border-[var(--border)] px-3 py-2 text-xs font-medium text-[var(--text-3)] transition hover:border-blue-500/30 hover:text-blue-500"
            >
              <Plus className="h-3.5 w-3.5" /> Add Location
            </button>
          )}
          <button
            onClick={runLookup}
            disabled={loading || !zipInputs.some((z) => z.length === 5)}
            className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Look Up Rates
          </button>
        </div>

        {error && (
          <p className="mt-3 rounded-lg bg-red-500/10 px-4 py-2 text-xs text-red-500">{error}</p>
        )}
      </div>

      {/* Empty state guidance */}
      {results.length === 0 && !loading && (
        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--surface-2)]/30 px-8 py-12 text-center">
          <MapPin className="mx-auto h-10 w-10 text-[var(--text-3)] opacity-40" />
          <p className="mt-4 text-sm font-semibold text-[var(--text-2)]">
            Enter up to 5 ZIP codes to compare BAH rates side by side
          </p>
          <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-[var(--text-3)]">
            See monthly and annual housing allowance for every rank at each location.
            Add multiple ZIPs to get a visual comparison chart — great for deciding
            between duty stations or planning a PCS move.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {['76544 — Fort Cavazos', '92101 — San Diego', '96860 — Pearl Harbor'].map((example) => (
              <span key={example} className="rounded-full border border-[var(--border)] bg-[var(--surface-1)] px-3 py-1 text-[10px] font-medium text-[var(--text-3)]">
                {example}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Quick rank selector */}
      {results.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-3)]">
            Highlight:
          </span>
          {HIGHLIGHT_RANKS.map((r) => (
            <button
              key={r}
              onClick={() => setSelectedRank(r)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                selectedRank === r
                  ? 'bg-blue-600 text-white'
                  : 'border border-[var(--border)] bg-[var(--surface-2)] text-[var(--text-3)] hover:text-[var(--text-1)]'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-6">
          {/* Comparison chart when 2+ locations */}
          {results.length >= 2 && (
            <ComparisonChart locations={results} selectedRank={selectedRank} />
          )}

          {/* Rate tables */}
          <div className={`grid gap-6 ${results.length > 1 ? 'lg:grid-cols-2' : ''}`}>
            {results.map((res, idx) => (
              <RateTable
                key={res.zip}
                result={res}
                highlightRank={selectedRank}
                onSelectRank={setSelectedRank}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
